import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import LyricConfirm from './LyricConfirm';
import './EmailApiModal.css';

/**
 * Owner/admin panel to manage a deployed app's Email API access.
 *
 * Lets the app owner enable the Email API (mints a per-app key), view/copy/
 * rotate that key, disable it, and copy a ready-to-run usage snippet. The app
 * then POSTs to /api/email/send with the key to send mail through Lyric's
 * shared SMTP relay (internal recipients only). See server/routes/email.js.
 *
 * Props:
 *   appName     registry key of the app
 *   displayName friendly name (for the header + snippet)
 *   apiUrl      API base URL
 *   authFetch   (url, opts) => fetch, injects the Okta bearer token
 *   onClose     close the modal
 */
const EmailApiModal = ({ appName, displayName, apiUrl, authFetch, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null); // { emailEnabled, apiKey, smtpConfigured, allowedDomain, rateLimit }
  const [busy, setBusy] = useState(false);
  const [revealKey, setRevealKey] = useState(false);
  const [copied, setCopied] = useState('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [redeployNeeded, setRedeployNeeded] = useState(false);
  const [redeploying, setRedeploying] = useState(false);
  const [applied, setApplied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/email`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, appName, authFetch]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape (matches the app-wide modal behavior).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirmRotate) onClose(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, confirmRotate]);

  const act = useCallback(async (path, label) => {
    setBusy(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/email/${path}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await load();
      if (label === 'rotate-key' || label === 'enable') setRevealKey(true);
      // enable/rotate return redeployRequired when the app is live — its env is
      // only re-read on respawn, so surface the "Redeploy now" prompt.
      if (label === 'disable') { setRedeployNeeded(false); setApplied(false); }
      else { setRedeployNeeded(!!data.redeployRequired); setApplied(false); }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [apiUrl, appName, authFetch, load]);

  const applyRedeploy = useCallback(async () => {
    setRedeploying(true);
    setError(null);
    try {
      const res = await authFetch(`${apiUrl}/api/apps/${encodeURIComponent(appName)}/email/apply`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRedeployNeeded(false);
      setApplied(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setRedeploying(false);
    }
  }, [apiUrl, appName, authFetch]);

  const copy = useCallback(async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard blocked — user can select manually */ }
  }, []);

  const key = status?.apiKey || '';
  const enabled = !!status?.emailEnabled;
  const maskedKey = key ? `${key.slice(0, 8)}${'•'.repeat(Math.max(0, key.length - 12))}${key.slice(-4)}` : '';
  const sendUrl = `${apiUrl}/api/email/send`;

  const curlSnippet = key
    ? `curl -X POST ${sendUrl} \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "someone@ischemaview.com",
    "subject": "Hello from ${appName}",
    "html": "<p>Sent via Lyric SMTP</p>"
  }'`
    : '';

  return ReactDOM.createPortal(
    <div className="email-api-overlay" onClick={(e) => { if (e.target === e.currentTarget && !confirmRotate) onClose(); }}>
      <div className="email-api-card" role="dialog" aria-modal="true" aria-label="Email API settings">
        <div className="email-api-header">
          <span className="email-api-brand" aria-hidden="true">✉️</span>
          <div>
            <h3>Email API</h3>
            <div className="email-api-sub">{displayName || appName}</div>
          </div>
          <button className="email-api-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="email-api-body">
          {loading ? (
            <div className="email-api-muted">Loading…</div>
          ) : error ? (
            <div className="email-api-error">{error}</div>
          ) : (
            <>
              {status && !status.smtpConfigured && (
                <div className="email-api-warn">
                  ⚠️ SMTP isn’t configured on the Lyric server yet — enabling here works, but sends will
                  fail until an admin sets up SMTP.
                </div>
              )}

              <div className="email-api-row">
                <span className="email-api-label">Status</span>
                <span className={`email-api-badge ${enabled ? 'on' : 'off'}`}>
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              {enabled && key && (
                <div className="email-api-keybox">
                  <span className="email-api-label">API key</span>
                  <code className="email-api-key">{revealKey ? key : maskedKey}</code>
                  <div className="email-api-keybtns">
                    <button onClick={() => setRevealKey((v) => !v)}>{revealKey ? 'Hide' : 'Reveal'}</button>
                    <button onClick={() => copy(key, 'key')}>{copied === 'key' ? 'Copied!' : 'Copy'}</button>
                  </div>
                </div>
              )}

              {enabled && key && (
                <div className="email-api-inject">
                  ✓ Lyric injects this key into your app automatically as the{' '}
                  <code>LYRIC_EMAIL_API_KEY</code> environment variable — no secret to set by hand.
                  It takes effect on the app’s next (re)deploy.
                </div>
              )}

              {redeployNeeded && (
                <div className="email-api-redeploy">
                  <div className="email-api-redeploy-text">
                    Redeploy <b>{displayName || appName}</b> to apply the key to the running app.
                  </div>
                  <button className="email-api-btn primary" disabled={redeploying} onClick={applyRedeploy}>
                    {redeploying ? 'Redeploying…' : 'Redeploy now'}
                  </button>
                </div>
              )}

              {applied && !redeployNeeded && (
                <div className="email-api-inject">
                  🚀 Redeploy started — the key is injected on respawn. Give it a moment, then test a send.
                </div>
              )}

              <div className="email-api-meta">
                {status?.allowedDomain && <span>Recipients: <b>@{status.allowedDomain}</b> only</span>}
                {status?.rateLimit && (
                  <span>Limit: <b>{status.rateLimit.max}</b> / {Math.round((status.rateLimit.windowMs || 0) / 60000)} min</span>
                )}
              </div>

              {enabled && key && (
                <div className="email-api-snippet">
                  <div className="email-api-snippet-head">
                    <span className="email-api-label">Usage</span>
                    <button onClick={() => copy(curlSnippet, 'curl')}>{copied === 'curl' ? 'Copied!' : 'Copy'}</button>
                  </div>
                  <pre>{curlSnippet}</pre>
                  <div className="email-api-muted small">
                    From is locked to the Lyric mailbox; your app appears as the sender’s display name.
                    Provide <code>to</code>, <code>subject</code>, and <code>html</code> or <code>text</code>.
                    In code, read the key from <code>process.env.LYRIC_EMAIL_API_KEY</code> (or your
                    language’s equivalent) and POST to <code>/api/email/send</code>.
                  </div>
                </div>
              )}

              <div className="email-api-actions">
                {!enabled ? (
                  <button className="email-api-btn primary" disabled={busy} onClick={() => act('enable', 'enable')}>
                    {busy ? 'Enabling…' : 'Enable Email API'}
                  </button>
                ) : (
                  <>
                    <button className="email-api-btn" disabled={busy} onClick={() => setConfirmRotate(true)}>
                      Rotate key
                    </button>
                    <button className="email-api-btn danger" disabled={busy} onClick={() => act('disable', 'disable')}>
                      {busy ? 'Disabling…' : 'Disable'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <LyricConfirm
        isOpen={confirmRotate}
        tone="danger"
        title="Rotate API key?"
        message="The current key stops working immediately. Any app or script using it must be updated with the new key."
        confirmLabel="Rotate key"
        onConfirm={() => { setConfirmRotate(false); act('rotate-key', 'rotate-key'); }}
        onCancel={() => setConfirmRotate(false)}
      />
    </div>,
    document.body,
  );
};

export default EmailApiModal;
